/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

// TorQE Logo Component - Sliced Shield V1
// Use this component throughout the app for consistent branding

import { useState } from "react";
import { cn } from "@/lib/utils";

interface TorqeLogoProps {
  size?: number;
  color?: string;
  variant?: "default" | "white" | "icon-only";
  className?: string;
  /** Uploaded logo from System Settings. Falls back to the built-in mark when absent or broken,
   *  so a deployment that has never uploaded one is unaffected. */
  src?: string | null;
  /** Accessible name. Defaults to the app name the caller is already displaying. */
  alt?: string;
}

export function TorqeLogo({
  size = 48,
  color = "#c74c00",
  variant = "default",
  className = "",
  src = null,
  alt = "",
}: TorqeLogoProps) {
  // A custom logo has to survive a bad URL: branding is stored as a free-text path, so a typo or a
  // deleted file would otherwise leave the header empty with no clue why. Falling back to the
  // built-in mark keeps the app looking intact.
  //
  // The failed URL is remembered, not a boolean, so the comparison below is derived from the
  // current prop rather than reset by an effect. With a boolean plus an effect, a src changing
  // from broken to working renders once with the stale flag still set -- the built-in mark flashes
  // for a frame before the effect clears it.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  // The <img> branch below gets its accessible name from `alt`; the SVG fallbacks had none, so a
  // logo URL that 404s silently dropped the name. Masked in the expanded sidebar, where the app
  // name is also rendered as adjacent text -- not masked when it is collapsed and the mark is the
  // only thing in the slot. With no `alt` the mark stays decorative rather than announcing an
  // image a screen reader can say nothing useful about.
  const svgLabel = alt
    ? ({ role: "img", "aria-label": alt } as const)
    : ({ "aria-hidden": true } as const);

  if (src && failedSrc !== src) {
    return (
      <img
        src={src}
        alt={alt}
        width={size}
        height={size}
        className={cn("object-contain", className)}
        style={{ width: size, height: size }}
        onError={() => {
          setFailedSrc(src);
        }}
      />
    );
  }

  if (variant === "white") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...svgLabel}
        className={className}
      >
        <path d="M24 4L44 11V13L24 20L4 13V11L24 4Z" fill="white" fillOpacity="0.95" />
        <path d="M24 20L44 13V21L24 28L4 21V13L24 20Z" fill="white" fillOpacity="0.6" />
        <path
          d="M24 28L44 21V30C44 36 36 44 24 48C12 44 4 36 4 30V21L24 28Z"
          fill="white"
          fillOpacity="0.35"
        />
        <path d="M18 7H30V10H26V17H22V10H18V7Z" fill="currentColor" />
      </svg>
    );
  }

  if (variant === "icon-only") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...svgLabel}
        className={className}
      >
        <path d="M24 4L44 11V13L24 20L4 13V11L24 4Z" fill={color} fillOpacity="0.95" />
        <path d="M24 20L44 13V21L24 28L4 21V13L24 20Z" fill={color} fillOpacity="0.6" />
        <path
          d="M24 28L44 21V30C44 36 36 44 24 48C12 44 4 36 4 30V21L24 28Z"
          fill={color}
          fillOpacity="0.35"
        />
        <path d="M18 7H30V10H26V17H22V10H18V7Z" fill="white" />
      </svg>
    );
  }

  // Default - with background
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...svgLabel}
      className={className}
    >
      <rect width="48" height="48" rx="12" fill={color} />
      <path d="M24 8L38 13V15L24 20L10 15V13L24 8Z" fill="white" fillOpacity="0.95" />
      <path d="M24 20L38 15V21L24 26L10 21V15L24 20Z" fill="white" fillOpacity="0.6" />
      <path
        d="M24 26L38 21V27C38 31 34 36 24 40C14 36 10 31 10 27V21L24 26Z"
        fill="white"
        fillOpacity="0.35"
      />
      <path d="M20 10.5H28V12.5H25V17H23V12.5H20V10.5Z" fill={color} />
    </svg>
  );
}

// Animated spinner version
export function TorqeSpinner({
  size = 48,
  color = "#c74c00",
  className = "",
}: Omit<TorqeLogoProps, "variant">) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <rect width="48" height="48" rx="12" fill={color} />
      <path
        d="M24 8L38 13V15L24 20L10 15V13L24 8Z"
        fill="white"
        className="animate-pulse"
        style={{ animationDelay: "0ms" }}
      />
      <path
        d="M24 20L38 15V21L24 26L10 21V15L24 20Z"
        fill="white"
        fillOpacity="0.6"
        className="animate-pulse"
        style={{ animationDelay: "150ms" }}
      />
      <path
        d="M24 26L38 21V27C38 31 34 36 24 40C14 36 10 31 10 27V21L24 26Z"
        fill="white"
        fillOpacity="0.35"
        className="animate-pulse"
        style={{ animationDelay: "300ms" }}
      />
    </svg>
  );
}

export default TorqeLogo;
